// A car's connections, as its TUNE MFD set them up: read here, changed on the MFD. Each says what it is, how it's
// connected, and for a CANBOX its port, bus speed and configs, by name: the owner's own open in the library.
import { useQuery } from '@tanstack/react-query';
import type { components } from '@tune/shared';
import { data } from '../../api';
import { useAuth } from '../../auth';
import { agoText } from '../../lib/words';
import { keys } from '../../queries';
import { EmptyState, ErrorBanner, ListLink, ListSection, ListValue, Skeleton, useNow } from '../../ui';
import { PlugIcon } from '../../ui/icons';
import { configIdOf, DEVICES } from '../values/connections';
import { CarPage } from './CarPage';
import s from './Sections.module.css';

type Vehicle = components['schemas']['Vehicle'];
type Item = components['schemas']['LibraryItem'];

/** A connection as the MFD keeps it (TUNE Live's connections.md), as far as this page shows it. */
interface Connection {
  name?: string;
  device?: string;
  link?: Record<string, Record<string, unknown> | undefined>;
  enabled?: boolean;
  canbox?: { port?: number; speed?: number | null; definitions?: string[] } | null;
}

/** How it's connected, in words. */
export function linkWords(c: Connection): string {
  const l = c.link ?? {};
  if (l.usb) {
    return 'USB';
  }
  if (l.bluetooth) {
    const address = String(l.bluetooth.address ?? '');
    return address ? `Bluetooth, ${address}` : 'Bluetooth';
  }
  if (l.tcp) {
    return `Wi-Fi, ${String(l.tcp.host ?? '')}:${String(l.tcp.port ?? '')}`;
  }
  if (l.serial) {
    return `Serial port, ${String(l.serial.path ?? '')}`;
  }
  return 'Not set';
}

/** A config a connection names: from the car's library when it's there, else as the MFD keeps it. */
export function configOf(key: string, library: readonly Item[]): { name: string; item: Item | null } {
  const id = configIdOf(key);
  const item = library.find((i) => i.kind === 'config' && i.identity.toLowerCase() === id) ?? null;
  if (item) {
    return { name: item.name, item };
  }
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(key)) {
    return { name: 'A config on the MFD', item: null };
  }
  // An older config's file, without its ending.
  return { name: key.replace(/^builtin:/, '').replace(/\.[^.]*$/, ''), item: null };
}

export function Connections() {
  return <CarPage section="Connections">{(car) => <ConnectionsOf car={car} />}</CarPage>;
}

function ConnectionsOf({ car }: { car: Vehicle }) {
  const api = useAuth().api('owners');
  const now = useNow(60_000);
  const path = { params: { path: { vehicleId: car.id } } };
  const connections = useQuery({
    queryKey: keys.connections(car.id),
    queryFn: () => data(api.GET('/vehicles/{vehicleId}/connections', path)),
  });
  const library = useQuery({
    queryKey: keys.vehicleLibrary(car.id),
    queryFn: () => data(api.GET('/vehicles/{vehicleId}/library', path)),
  });
  if (connections.isPending) {
    return <Skeleton blocks={2} lines={0} label="Loading the connections" />;
  }
  if (connections.isError) {
    return <ErrorBanner retry={() => void connections.refetch()}>The connections couldn't load.</ErrorBanner>;
  }
  const { items, from, updatedAt } = connections.data;
  const known = library.data?.items ?? [];
  return (
    <>
      <p className={s.lead}>
        Set up on the car's TUNE MFD, and shown here.
        {from && updatedAt ? ` The MFD sent them ${agoText(updatedAt, now)}.` : ''}
      </p>
      {items.length === 0 && (
        <EmptyState icon={<PlugIcon size={56} />} title="No connections yet">
          The MFD sends them once it has some: what reads the car, like a CANBOX or an OBD2 adapter.
        </EmptyState>
      )}
      <div className={s.grid}>
        {items.map((item) => {
          const c = item.connection as Connection;
          const box = c.device === 'canbox' ? c.canbox : null;
          return (
            <ListSection
              key={item.id}
              header={c.name || DEVICES[c.device ?? ''] || 'A connection'}
              footer={c.enabled === false ? "Off: the MFD doesn't read it." : undefined}
            >
              <ListValue label="Device" detail={DEVICES[c.device ?? ''] ?? 'Unknown'} />
              <ListValue label="Connected by" detail={linkWords(c)} />
              {box && <ListValue label="Port" detail={box.port === 1 ? 'CAN2' : 'CAN1'} />}
              {box && <ListValue label="Bus speed" detail={box.speed ? `${box.speed} kbit/s` : 'Found by searching'} />}
              {box && (box.definitions ?? []).length === 0 && <ListValue label="Configs" detail="None" />}
              {(box?.definitions ?? []).map((key, i, all) => {
                const config = configOf(key, known);
                const label = all.length === 1 ? 'Config' : `Config ${i + 1}`;
                return config.item?.source.kind === 'own' ? (
                  <ListLink key={key} to={`/library/${config.item.id}`} label={label} detail={config.name} />
                ) : (
                  <ListValue key={key} label={label} detail={config.name} />
                );
              })}
            </ListSection>
          );
        })}
      </div>
    </>
  );
}
